/**
 * Chat Session Reducers
 * 
 * Splits chat state into two concerns:
 * 1. Data reducer: messages, session, branches (what data exists)
 * 2. UI reducer: dialogs, editing states (how UI is displayed)
 * 
 * This separation makes testing easier and clarifies responsibilities.
 */

import { 
  ChatUIState, 
  ChatUIAction,
  Message,
  Session
} from '@/types/state';


// ============================================================================
// Chat UI Reducer (Client-side UI state)
// ============================================================================

export const initialChatUIState: ChatUIState = {
  input: '',
  sending: false,
  showFrameworkWidget: false,
  isTitleGenerating: false,
  editingMessageId: null,
  editingContent: '',
  regeneratingMessageId: null,
  showRegenerateDialog: false,
  messageToRegenerate: null,
  showBookmarkPanel: false,
  branchDialogOpen: false,
  branchingFromMessage: null,
  branchListOpen: false,
  mergeDialogOpen: false,
  selectedMergeBranch: null,
};

export function chatUIReducer(state: ChatUIState, action: ChatUIAction): ChatUIState {
  if (import.meta.env.DEV) {
    console.log('[ChatUIReducer]', action.type, action);
  }

  switch (action.type) {
    case 'SET_INPUT':
      return {
        ...state,
        input: action.payload,
      };

    case 'SET_SENDING':
      return {
        ...state,
        sending: action.payload,
      };

    case 'TOGGLE_FRAMEWORK_WIDGET':
      return {
        ...state,
        showFrameworkWidget: !state.showFrameworkWidget,
      };

    case 'SET_TITLE_GENERATING':
      return {
        ...state,
        isTitleGenerating: action.payload,
      };

    case 'START_EDITING':
      return {
        ...state,
        editingMessageId: action.payload.messageId,
        editingContent: action.payload.content,
      };

    case 'UPDATE_EDITING_CONTENT':
      return {
        ...state,
        editingContent: action.payload,
      };

    case 'CANCEL_EDITING':
      return {
        ...state,
        editingMessageId: null,
        editingContent: '',
      };

    case 'START_REGENERATING':
      return {
        ...state,
        showRegenerateDialog: true,
        messageToRegenerate: action.payload,
      };

    case 'CANCEL_REGENERATING':
      return {
        ...state,
        showRegenerateDialog: false,
        messageToRegenerate: null,
        regeneratingMessageId: null,
      };

    case 'SET_REGENERATING_MESSAGE_ID':
      return {
        ...state,
        regeneratingMessageId: action.payload,
      };

    case 'TOGGLE_BOOKMARK_PANEL':
      return {
        ...state,
        showBookmarkPanel: !state.showBookmarkPanel,
      };

    case 'START_BRANCHING':
      return {
        ...state,
        branchDialogOpen: true,
        branchingFromMessage: action.payload,
      };

    case 'CANCEL_BRANCHING':
      return {
        ...state,
        branchDialogOpen: false,
        branchingFromMessage: null,
      };

    case 'TOGGLE_BRANCH_LIST':
      return {
        ...state,
        branchListOpen: !state.branchListOpen,
      };

    case 'START_MERGING':
      return {
        ...state,
        mergeDialogOpen: true,
        selectedMergeBranch: action.payload,
      };

    case 'CANCEL_MERGING':
      return {
        ...state,
        mergeDialogOpen: false,
        selectedMergeBranch: null,
      };

    case 'RESET_CHAT_UI':
      return initialChatUIState;

    default:
      const _exhaustive: never = action;
      console.warn('[ChatUIReducer] Unknown action:', _exhaustive);
      return state;
  }
}

// ============================================================================
// Action Creators
// ============================================================================


export const chatUIActions = {
  setInput: (payload: string): ChatUIAction => ({
    type: 'SET_INPUT',
    payload,
  }),

  setSending: (payload: boolean): ChatUIAction => ({
    type: 'SET_SENDING',
    payload,
  }),

  toggleFrameworkWidget: (): ChatUIAction => ({
    type: 'TOGGLE_FRAMEWORK_WIDGET',
  }),

  setTitleGenerating: (payload: boolean): ChatUIAction => ({
    type: 'SET_TITLE_GENERATING',
    payload,
  }),

  startEditing: (messageId: string, content: string): ChatUIAction => ({
    type: 'START_EDITING',
    payload: { messageId, content },
  }),

  updateEditingContent: (payload: string): ChatUIAction => ({
    type: 'UPDATE_EDITING_CONTENT',
    payload,
  }),

  cancelEditing: (): ChatUIAction => ({
    type: 'CANCEL_EDITING',
  }),

  startRegenerating: (payload: Message): ChatUIAction => ({
    type: 'START_REGENERATING',
    payload,
  }),

  cancelRegenerating: (): ChatUIAction => ({
    type: 'CANCEL_REGENERATING',
  }),

  setRegeneratingMessageId: (payload: string | null): ChatUIAction => ({
    type: 'SET_REGENERATING_MESSAGE_ID',
    payload,
  }),

  toggleBookmarkPanel: (): ChatUIAction => ({
    type: 'TOGGLE_BOOKMARK_PANEL',
  }),

  startBranching: (payload: Message): ChatUIAction => ({
    type: 'START_BRANCHING',
    payload,
  }),

  cancelBranching: (): ChatUIAction => ({
    type: 'CANCEL_BRANCHING',
  }),

  toggleBranchList: (): ChatUIAction => ({
    type: 'TOGGLE_BRANCH_LIST',
  }),

  startMerging: (payload: Session): ChatUIAction => ({
    type: 'START_MERGING',
    payload,
  }),

  cancelMerging: (): ChatUIAction => ({
    type: 'CANCEL_MERGING',
  }),

  resetChatUI: (): ChatUIAction => ({
    type: 'RESET_CHAT_UI',
  }),
};
